import React from 'react';
import { resolveElementStyle, StylePart } from './stylesConfig';

// Consumer-side mirror of the admin editor's search page config
// (ac-main/web: components/Storefront/Editor/searchPageConfig.ts). Kept in sync so
// the storefront renders exactly what the "Customize Page" preview shows. The config
// is persisted as a single JSON string on IStorefrontSettings.searchPageConfig.

export type SearchResultViewMode = 'rows' | 'cards';

// The synthetic "Featured" tag. Featured status is per-storefront (not a stored product tag),
// so it's surfaced as a normal-looking tag chip and, in the URL, rides as ?tag=Featured — but
// the search page translates it into the `featured` search filter rather than a tag terms match.
export const FEATURED_TAG = 'Featured';

// Where the facet-filter controls render on the search page. 'panel' = a side panel
// beside the results; 'inline' = a row of menu controls in the breadcrumb/chip area.
export type FacetSidebarLayout = 'panel' | 'inline';

// Shopper-facing facet filtering. Surfaced only once a segment AND category are
// selected, then shows the storefront's searchable attributes (aggregated over that
// segment/category subset) as filter controls, plus an optional price-range filter.
export interface IFacetSidebarConfig {
    enabled?: boolean;
    layout?: FacetSidebarLayout;
    priceFilter?: boolean;
}

// The named fields below get first-class controls in the admin editor and a bit of
// smart defaulting in the builders. The index signature lets the admin "Raw JSON"
// mode add any additional CSS property (camelCase, e.g. boxShadow / filter /
// transition) — those pass straight through to the element via importantStyleRef.
export interface ISearchCardStyle {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderStyle?: string;
    borderRadius?: number;
    [prop: string]: string | number | undefined;
}

export interface ISearchTextStyle {
    font?: string;
    fontSize?: number; // percent, matching the storefront's % font sizing
    fontWeight?: string;
    color?: string;
    [prop: string]: string | number | undefined;
}

export interface ISearchPriceStyle extends ISearchTextStyle {
    // The price can optionally be wrapped in its own styled container.
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    padding?: string;
}

// The box that wraps the whole results grid (the `.card-container`). Background,
// border and inner padding styling; card spacing lives in cardGap.
export interface ISearchContainerStyle {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderStyle?: string;
    borderRadius?: number;
    padding?: number;
    [prop: string]: string | number | undefined;
}

// Generic text + box style used for the paginator and breadcrumb elements (each of
// which has a container plus repeated cells/items).
export interface ISearchElementStyle {
    font?: string;
    fontSize?: number; // percent
    fontWeight?: string;
    color?: string;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderStyle?: string;
    borderRadius?: number;
    // A bare number is px, but merchants routinely author the CSS shorthand instead
    // ("0.5em 1.5em" on a banner CTA, so the padding tracks the button's font size).
    // Readers that need the components already branch on the string form — see
    // hasZeroLeftInset and ProductCard's price inset.
    padding?: string | number;
    // First-class controls for a few common camelCase CSS props (also valid via the
    // passthrough index signature below); named here so the editor controls can read them.
    textTransform?: string;
    textAlign?: string;
    letterSpacing?: string;
    boxShadow?: string;
    [prop: string]: string | number | undefined;
}

export interface ISearchPageConfig {
    viewMode?: SearchResultViewMode;
    // Gap between cards in the results grid, in px (falls back to the CSS default, 16).
    cardGap?: number;
    card?: ISearchCardStyle;
    title?: ISearchTextStyle;
    price?: ISearchPriceStyle;
    // The product image area within each card/row (background tint, border, radius, padding).
    image?: ISearchElementStyle;
    container?: ISearchContainerStyle;
    // Paginator: the menu box (container) and each page button (cell).
    paginatorContainer?: ISearchElementStyle;
    paginatorCell?: ISearchElementStyle;
    // Filter breadcrumbs: the row (container) and each chip (item).
    breadcrumbContainer?: ISearchElementStyle;
    breadcrumbItem?: ISearchElementStyle;
    // Per-result-row meta chips (rows view). The whole "Category: A > B > C" breadcrumb
    // and the whole "Manufacturer: X" label each render as a single styled chip (bg,
    // border, radius, padding, font). The clickable links inside keep their own behavior.
    categoryChip?: ISearchElementStyle;
    manufacturerChip?: ISearchElementStyle;
    // Shopper-facing facet filtering (attribute + optional price filters).
    facetSidebar?: IFacetSidebarConfig;
    // When true, the search "chrome" (the filter panel, the search bar and the paginator)
    // stays pinned while scrolling so only the results scroll underneath.
    stickyControls?: boolean;
    // The search keyword input box (Fluent Input root).
    searchBar?: ISearchElementStyle;
    // The facet-filter side panel container (the 'panel' layout aside).
    facetPanel?: ISearchElementStyle;
    // The facet section headings inside the panel (each attribute group's title, e.g. "RAM").
    // Applied as a scoped rule so it beats Fluent's forced Text color.
    facetHeading?: ISearchTextStyle;
    // The circular "add to cart" button shown on each result (rows + cards views).
    addToCartButton?: ISearchElementStyle;
    // The "Add to Cart" text label beside that button (rows view). Defaults to a white pill with
    // green text; override to fit non-green themes.
    addToCartLabel?: ISearchElementStyle;
    // Hover styling. Applied as scoped `.selector:hover` rules (an inline style prop can't
    // express `:hover`), so any CSS passes through — boxShadow, transform, borderColor,
    // backgroundColor, filter, etc. A short transition is added automatically so the change
    // animates. `cardHover` covers the product tile in both the cards and rows views.
    cardHover?: ISearchElementStyle;
}

export const DEFAULT_SEARCH_PAGE_CONFIG: ISearchPageConfig = {
    viewMode: 'rows',
};

export function parseSearchPageConfig(raw?: string): ISearchPageConfig {
    if (!raw) return { ...DEFAULT_SEARCH_PAGE_CONFIG };
    try {
        const parsed = JSON.parse(raw) as ISearchPageConfig;
        return { ...DEFAULT_SEARCH_PAGE_CONFIG, ...parsed };
    } catch {
        return { ...DEFAULT_SEARCH_PAGE_CONFIG };
    }
}

// Style builders — identical output to the admin editor so the live page matches the
// preview. Each returns only the properties the merchant actually set (undefined
// elsewhere), so unset values fall through to the default CSS in index.css.
//
// Every builder first runs its input through resolveElementStyle (see stylesConfig.ts): a dict
// carrying the reserved `$styleRef` key gets the referenced named style's PART merged underneath it
// (local wins) and the key stripped, so `$styleRef` can never reach a style attribute. The optional
// `part` argument names the role this dict plays — buttons resolve `base`, an add-to-cart label
// `label`, a card `card`, a card title `title`, a form input `input`, a page heading `heading`, and
// so on. A dict with no `$styleRef` (or a storefront with no styles) comes back BY IDENTITY, so
// these builders behave exactly as they did before the feature.
export const cardStyleFrom = (card?: ISearchCardStyle, part: StylePart | string = 'card'): React.CSSProperties => {
    card = resolveElementStyle(card, part);
    if (!card) return {};
    // Pull out the border props that get smart defaulting; `...rest` carries
    // backgroundColor and any raw-edit CSS (boxShadow, filter, …) straight through.
    const { borderColor, borderWidth, borderStyle, borderRadius, ...rest } = card;
    const hasBorder = !!borderColor || borderWidth != null || (!!borderStyle && borderStyle !== '');
    return {
        ...(rest as React.CSSProperties),
        borderColor: borderColor || undefined,
        borderWidth: borderWidth != null ? borderWidth : undefined,
        borderStyle: borderStyle ? String(borderStyle) : (hasBorder ? 'solid' : undefined),
        borderRadius: borderRadius != null ? borderRadius : undefined,
    };
};

// The box around the whole grid. Same border-defaulting/passthrough as the card;
// backgroundColor and padding ride through `...rest`.
export const containerStyleFrom = (c?: ISearchContainerStyle, part: StylePart | string = 'band'): React.CSSProperties => {
    c = resolveElementStyle(c, part);
    if (!c) return {};
    const { borderColor, borderWidth, borderStyle, borderRadius, ...rest } = c;
    const hasBorder = !!borderColor || borderWidth != null || (!!borderStyle && borderStyle !== '');
    return {
        ...(rest as React.CSSProperties),
        borderColor: borderColor || undefined,
        borderWidth: borderWidth != null ? borderWidth : undefined,
        borderStyle: borderStyle ? String(borderStyle) : (hasBorder ? 'solid' : undefined),
        borderRadius: borderRadius != null ? borderRadius : undefined,
    };
};

// Generic text + box builder for the paginator/breadcrumb elements: font/fontSize get
// the same transform as text styles, borders get the same defaulting as the card/box,
// and backgroundColor/color/padding/fontWeight (plus any raw-edit CSS) pass through.
export const elementStyleFrom = (s?: ISearchElementStyle, part: StylePart | string = 'base'): React.CSSProperties => {
    s = resolveElementStyle(s, part);
    if (!s) return {};
    const { font, fontSize, borderColor, borderWidth, borderStyle, borderRadius, ...rest } = s;
    const hasBorder = !!borderColor || borderWidth != null || (!!borderStyle && borderStyle !== '');
    const style: React.CSSProperties = {
        ...(rest as React.CSSProperties),
    };
    // Only emit the font longhands when they carry a value, for the same reason as the borders below.
    // This builder's output is spread OVER a resting style on hover (BannerOverlayCta), where an
    // emitted `fontSize: undefined` is a *clear*, not an absence: a hoverStyle that only recolored the
    // button silently reset the configured font and size to the UA default. With em-based padding the
    // button then shrank out from under the cursor, which unhovered it, regrew it under the cursor,
    // and strobed.
    if (font) style.fontFamily = font;
    if (fontSize != null) style.fontSize = `${fontSize}%`;
    // Only emit border longhands when they actually carry a value. Emitting `borderStyle: undefined`
    // (etc.) makes React set the DOM property to '' — a *clear* that also wipes any per-side shorthand
    // like `borderBottom` in `rest` down to `none`, so a configured bottom-divider silently vanishes.
    if (borderColor) style.borderColor = borderColor;
    if (borderWidth != null) style.borderWidth = borderWidth;
    if (borderStyle) style.borderStyle = String(borderStyle);
    else if (hasBorder) style.borderStyle = 'solid';
    if (borderRadius != null) style.borderRadius = borderRadius;
    return style;
};

export const textStyleFrom = (t?: ISearchTextStyle, part: StylePart | string = 'body'): React.CSSProperties => {
    t = resolveElementStyle(t, part);
    if (!t) return {};
    // `font` maps to fontFamily and `fontSize` is a percent; everything else
    // (fontWeight, color, and any raw-edit CSS) passes straight through.
    const { font, fontSize, ...rest } = t;
    return {
        ...(rest as React.CSSProperties),
        fontFamily: font || undefined,
        fontSize: fontSize != null ? `${fontSize}%` : undefined,
    };
};

export const priceStyleFrom = (p?: ISearchPriceStyle, part: StylePart | string = 'price'): React.CSSProperties => {
    // Resolve here; the nested textStyleFrom call below then sees a ref-free dict (no double work).
    p = resolveElementStyle(p, part);
    if (!p) return {};
    const { borderColor, borderWidth, borderStyle, borderRadius, backgroundColor, padding, ...textRest } = p;
    const hasBorder = !!borderColor || borderWidth != null || (!!borderStyle && borderStyle !== '');
    const hasContainer = !!backgroundColor || hasBorder || !!padding;
    return {
        ...textStyleFrom(textRest),
        backgroundColor: backgroundColor || undefined,
        borderColor: borderColor || undefined,
        borderWidth: borderWidth != null ? borderWidth : undefined,
        borderStyle: borderStyle ? String(borderStyle) : (hasBorder ? 'solid' : undefined),
        borderRadius: borderRadius != null ? borderRadius : undefined,
        padding: padding || undefined,
        display: hasContainer ? 'inline-block' : undefined,
    };
};

// The dark/light themes force `.card`, `.card .fui-Text` and `.card .fui-Caption1`
// colors and borders with `!important` (see public/themes/custom_dark.css), which
// silently overrides the merchant's configured search-card styling when it's applied
// as a plain inline `style` prop. A React `style` prop can't emit `!important`, so we
// apply the computed style through a ref that uses setProperty(..., 'important') —
// inline `!important` outranks any author-stylesheet `!important`, so the merchant's
// choices win. Returns a ref callback for any DOM element or forwardRef'd Fluent
// component (e.g. Card). Fluent's Text/Caption1 *presets* don't forward a ref, so wrap
// those in a plain <span> that carries the color and let the preset inherit it.
export const importantStyleRef = (style: React.CSSProperties): React.RefCallback<HTMLElement> =>
    (el) => {
        if (!el) return;
        for (const [key, value] of Object.entries(style)) {
            if (value == null) continue;
            const cssProp = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
            // The style builders only emit bare numbers for pixel lengths (border width/radius).
            const cssVal = typeof value === 'number' ? `${value}px` : String(value);
            el.style.setProperty(cssProp, cssVal, 'important');
        }
    };

// Same intent as importantStyleRef (emit every property with `!important` so it outranks
// the theme's forced `.card` / `.category-pod-button` rules), but as a CSS declaration
// string for injection through a scoped <style> rule instead of a ref. Use this when the
// target element's ref isn't reliably reachable — e.g. the category pods are wrapped in
// Fluent's <OverflowItem>, whose merged ref swallows the child button's ref callback, so
// importantStyleRef never runs on them. A stylesheet rule sidesteps the ref entirely and
// still updates reactively when the config changes.
export const importantStyleCss = (style: React.CSSProperties): string =>
    Object.entries(style)
        .filter(([, value]) => value != null)
        .map(([key, value]) => {
            const cssProp = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
            const cssVal = typeof value === 'number' ? `${value}px` : String(value);
            return `${cssProp}: ${cssVal} !important;`;
        })
        .join(' ');
